/**
 * Turning a userId into something a person can read (#2094).
 *
 * Membership rows across the app store only ids — a workspace grant's
 * `subjectId` and `grantedBy`, a team member's `userId`. The routes
 * that serve those rows attach a `users` sidecar map so the UI can
 * name them. This module owns the fallback chain applied to that map,
 * because three call sites need exactly the same one (the workspace
 * members table's Subject and Granted by columns, and the team member
 * list) and three independent copies would drift.
 *
 * The chain is name → email → the raw id. The last step matters: the
 * server's lookup is fail-soft by contract and omits users it cannot
 * resolve — a deleted account, a legacy row, or a transient database
 * failure that degraded the whole map to `{}`. In every one of those
 * cases the row must still render. Showing an id is a worse answer
 * than showing a name, but it is a far better answer than a blank
 * cell or a crashed table.
 *
 * Deliberately NOT folded in: the Admin → Audit page's actor
 * rendering. It formats differently (`email · truncated-id`, both
 * parts always shown) because an auditor is verifying identity rather
 * than reading a roster. Merging the two would mean a variant flag
 * serving one caller each, which is the kind of abstraction that
 * relocates a conditional instead of removing it.
 */
import type { UserDisplayMap } from '@/lib/api'

/**
 * The name to show for a user: their name, else their email address,
 * else the raw id. Never returns an empty string for a non-empty id —
 * a user row whose `name` is present but blank falls through to the
 * email rather than rendering as nothing.
 */
export function userDisplayName(users: UserDisplayMap | undefined, userId: string): string {
  const user = users?.[userId]
  const name = user?.name?.trim()
  if (name) return name
  const email = user?.email?.trim()
  if (email) return email
  return userId
}

/**
 * The secondary line beneath the name — the email address, but only
 * when it isn't already doing duty as the primary label. Returns null
 * when there is nothing to add, so callers can skip the element
 * entirely rather than rendering an empty one.
 */
export function userDisplaySecondary(
  users: UserDisplayMap | undefined,
  userId: string,
): string | null {
  const user = users?.[userId]
  const name = user?.name?.trim()
  const email = user?.email?.trim()
  if (!name || !email) return null
  return email
}

/**
 * True when we resolved a real identity for this id, i.e. the label
 * above is a name or email rather than the id itself. Callers use
 * this to decide whether a raw id is still on screen and needs the
 * full value in a tooltip.
 */
export function isUserResolved(users: UserDisplayMap | undefined, userId: string): boolean {
  const user = users?.[userId]
  return Boolean(user?.name?.trim() || user?.email?.trim())
}
